import { useState, useRef, useEffect, type ReactNode } from 'react'
import { Plus, X, MoreHorizontal, Trash2 } from 'lucide-react'
import {
  Button,
  Input,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@bpinternal/ui-kit-next'

// Filter configuration types
export type FilterFieldDefinition =
  | { type: 'string'; id: string; label: string; icon?: ReactNode }
  | { type: 'array'; id: string; label: string; icon?: ReactNode }
  | { type: 'key_value'; id: string; label: string; icon?: ReactNode }
  | { type: 'select'; id: string; label: string; icon?: ReactNode; options: SelectOption[] }

export type SelectOption = { value: string; label: string; icon?: ReactNode }

export type ActiveFilter = {
  id: string
  fieldId: string
  operator: string
  value: string
  key?: string
}

type Props = {
  fields: FilterFieldDefinition[]
  activeFilters: ActiveFilter[]
  onChange: (filters: ActiveFilter[]) => void
}

// --- Sub-Components ---

/**
 * The dropdown menu used to configure a specific active filter.
 */
interface FilterConfigMenuProps {
  filter: ActiveFilter
  fields: FilterFieldDefinition[]
  onUpdate: (updatedFilter: ActiveFilter) => void
  onDelete: () => void
  onClose: () => void
}

const FilterConfigMenu = ({ filter, fields, onUpdate, onDelete, onClose }: FilterConfigMenuProps) => {
  const currentField = fields.find((f) => f.id === filter.fieldId)
  const inputRef = useRef<HTMLInputElement>(null)
  const keyInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Focus logic
  useEffect(() => {
    if (!currentField) return
    // If it's a standard string filter, focus value
    if (currentField.type === 'string' && inputRef.current && !filter.value) {
      inputRef.current.focus()
    }
    // If it's a key_value filter, focus key first if empty
    if (currentField.type === 'key_value' && keyInputRef.current && !filter.key) {
      keyInputRef.current.focus()
    }
  }, [currentField, filter.key, filter.value])

  // Click outside to close main menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  if (!currentField) return null

  const isKeyValue = currentField.type === 'key_value'
  const isSelect = currentField.type === 'select'

  return (
    <div
      ref={menuRef}
      className="absolute left-0 top-full z-50 mt-1 flex w-56 flex-col rounded-lg border bg-background shadow-xl"
    >
      <div className="p-2">
        {/* Header: Field Selector and Actions */}
        <div className="mb-1 flex items-center justify-between text-xs">
          <span>{currentField.label}</span>
        </div>

        {/* Dynamic Inputs */}
        <div className="flex flex-col gap-2 text-xs">
          {isKeyValue ? (
            <>
              <Input
                ref={keyInputRef}
                type="text"
                value={filter.key || ''}
                onChange={(e) => onUpdate({ ...filter, key: e.target.value })}
                placeholder="Type a key..."
                className="h-8 text-sm"
              />
              <Input
                type="text"
                value={filter.value}
                onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
                placeholder="Type a value..."
                className="h-8 text-sm"
              />
            </>
          ) : isSelect && currentField.type === 'select' ? (
            <div className="flex flex-col gap-1">
              {currentField.options.map((option: SelectOption) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onUpdate({ ...filter, value: option.value })
                    onClose()
                  }}
                  className={`flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-accent ${
                    filter.value === option.value ? 'bg-accent' : ''
                  }`}
                >
                  {option.icon && <span>{option.icon}</span>}
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <Input
              ref={inputRef}
              type="text"
              value={filter.value}
              onChange={(e) => onUpdate({ ...filter, value: e.target.value })}
              placeholder="Type a value..."
              className="h-8 text-sm"
            />
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The main container component.
 */
export const FilterBar = ({ fields, activeFilters, onChange }: Props) => {
  const [openFilterId, setOpenFilterId] = useState<string | null>(null)

  // Handle adding a new filter
  const handleAddFilter = (fieldId: string) => {
    const newFilter: ActiveFilter = {
      id: crypto.randomUUID(),
      fieldId,
      operator: 'contains',
      value: '',
      key: '',
    }
    onChange([...activeFilters, newFilter])
    // Immediately open the config for the new filter
    setTimeout(() => setOpenFilterId(newFilter.id), 10)
  }

  // Handle updating a filter
  const handleUpdateFilter = (updatedFilter: ActiveFilter) => {
    const newFilters = activeFilters.map((f) => (f.id === updatedFilter.id ? updatedFilter : f))
    onChange(newFilters)
  }

  // Handle deleting a filter
  const handleDeleteFilter = (id: string) => {
    onChange(activeFilters.filter((f) => f.id !== id))
    setOpenFilterId(null)
  }

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-2">
        {/* Active Filters List */}
        {activeFilters.map((filter) => {
          const fieldDef = fields.find((f) => f.id === filter.fieldId)
          if (!fieldDef) return null

          const isOpen = openFilterId === filter.id
          const isKeyValue = fieldDef.type === 'key_value'
          const hasValue = isKeyValue ? filter.key || filter.value : filter.value

          return (
            <div key={filter.id} className="group relative">
              <div
                className={`flex h-7 max-w-[240px] items-center gap-1 rounded-full px-3 py-1 ${
                  hasValue ? 'bg-blue-50' : 'border border-dashed border-blue-300 bg-blue-50/40'
                }`}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex min-w-0 flex-1 items-center gap-1 rounded-full px-0 text-xs text-blue-600 hover:bg-transparent focus-visible:ring-0"
                  onClick={() => setOpenFilterId(isOpen ? null : filter.id)}
                >
                  <span className="flex-shrink-0 font-medium">
                    {fieldDef.label}
                    {hasValue ? ':' : ''}
                  </span>
                  {hasValue && (
                    <span className="min-w-0 truncate">
                      {isKeyValue ? (
                        <>
                          {filter.key && <span>{filter.key}</span>}
                          {filter.key && filter.value && <span>: </span>}
                          {filter.value && <span>{filter.value}</span>}
                        </>
                      ) : (
                        <span>{filter.value}</span>
                      )}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-1 h-4 w-4 flex-shrink-0 rounded-full text-blue-600 hover:bg-blue-100 focus-visible:ring-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDeleteFilter(filter.id)
                  }}
                >
                  <X size={12} />
                </Button>
              </div>

              {isOpen && (
                <div className="absolute left-0 top-full z-50">
                  <FilterConfigMenu
                    filter={filter}
                    fields={fields}
                    onUpdate={handleUpdateFilter}
                    onDelete={() => handleDeleteFilter(filter.id)}
                    onClose={() => setOpenFilterId(null)}
                  />
                </div>
              )}
            </div>
          )
        })}

        {/* Add Filter Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="flex h-7 items-center gap-1 rounded px-2 text-xs">
              <Plus size={16} />
              <span className="font-medium">Filter</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[300px] w-48 overflow-y-auto">
            {fields.map((field) => (
              <DropdownMenuItem
                key={field.id}
                className="text-xs"
                onSelect={() => {
                  handleAddFilter(field.id)
                }}
              >
                {field.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

// Export as FilterBuilder for backward compatibility
export const FilterBuilder = FilterBar
